import { memo } from 'react';

import {
    ACTIVITY_TYPE_ANNOTATION,
    ACTIVITY_TYPE_CALL,
    ACTIVITY_TYPE_CHECKIN,
    ACTIVITY_TYPE_EMAIL,
    ACTIVITY_TYPE_FAST_CHECKIN,
    ACTIVITY_TYPE_MAILCHIMP,
    ACTIVITY_TYPE_MARKETING_CAMPAIGN,
    ACTIVITY_TYPE_OPPORTUNITY_CHECKIN,
    ACTIVITY_TYPE_VIDEO_CALL,
    ACTIVITY_TYPE_VIDEO_CHECKIN,
    ACTIVITY_TYPE_WHATSAPP,
    ACTIVITY_TYPE_WORKFLOW,
    ACTIVITY_TYPE_ZENDESK,
} from 'models/ActivityModel';

import useActivityDetailSharedContent from './useActivityDetailSharedContent';
import AnnotationDetail from './types/AnnotationDetail';
import CallDetail from './types/CallDetail';
import CheckInDetail from './types/CheckInDetail';
import EmailGeneralDetail from './types/EmailGeneralDetail';
import IntegrationDetail from './types/IntegrationDetail';
import MarketingCampaignGeneralDetail from './types/MarketingCampaignGeneralDetail';
import VideoCallDetail from './types/VideoCallDetail';
import WhatsappGeneralDetail from './types/WhatsappGeneralDetail';
import WorkflowDetail from './types/WorkflowDetail';

const GeneralDetail = memo(
    ({
        activity,
        canEditActivity,
        emailWithImages,
        getDateText,
        getElapsedDateText,
        getEmailWithImages,
        getHeaderItems,
        getIsCommentCollapsed,
        hasTabs,
        isFromWidgetDetail,
        labels,
        loadingEmailWithImages,
        openTab,
        showActionsAndMenu,
    }) => {
        const shared = useActivityDetailSharedContent({
            activity,
            canEditActivity,
            getDateText,
            getElapsedDateText,
            getHeaderItems,
            hasTabs,
            isFromWidgetDetail,
            labels,
            openTab,
            showActionsAndMenu,
        });

        if (!activity) return null;

        switch (parseInt(activity.ActivityType, 10)) {
            case ACTIVITY_TYPE_ANNOTATION:
                return (
                    <AnnotationDetail
                        activity={activity}
                        getIsCommentCollapsed={getIsCommentCollapsed}
                        labels={labels}
                        shared={shared}
                    />
                );
            case ACTIVITY_TYPE_CHECKIN:
            case ACTIVITY_TYPE_OPPORTUNITY_CHECKIN:
            case ACTIVITY_TYPE_FAST_CHECKIN:
                return (
                    <CheckInDetail
                        activity={activity}
                        getIsCommentCollapsed={getIsCommentCollapsed}
                        labels={labels}
                        shared={shared}
                    />
                );
            case ACTIVITY_TYPE_CALL:
                return <CallDetail activity={activity} labels={labels} shared={shared} />;
            case ACTIVITY_TYPE_EMAIL:
                return (
                    <EmailGeneralDetail
                        activity={activity}
                        emailWithImages={emailWithImages}
                        getEmailWithImages={getEmailWithImages}
                        labels={labels}
                        loadingEmailWithImages={loadingEmailWithImages}
                        shared={shared}
                    />
                );
            case ACTIVITY_TYPE_WORKFLOW:
                return <WorkflowDetail activity={activity} />;
            case ACTIVITY_TYPE_VIDEO_CALL:
            case ACTIVITY_TYPE_VIDEO_CHECKIN:
                return (
                    <VideoCallDetail
                        activity={activity}
                        getIsCommentCollapsed={getIsCommentCollapsed}
                        labels={labels}
                        shared={shared}
                    />
                );
            case ACTIVITY_TYPE_MAILCHIMP:
            case ACTIVITY_TYPE_ZENDESK:
                return <IntegrationDetail activity={activity} labels={labels} shared={shared} />;
            case ACTIVITY_TYPE_MARKETING_CAMPAIGN:
                return (
                    <MarketingCampaignGeneralDetail
                        activity={activity}
                        labels={labels}
                        shared={shared}
                    />
                );
            case ACTIVITY_TYPE_WHATSAPP:
                return (
                    <WhatsappGeneralDetail
                        activity={activity}
                        getIsCommentCollapsed={getIsCommentCollapsed}
                        labels={labels}
                        shared={shared}
                    />
                );
            default:
                return null;
        }
    },
);

GeneralDetail.displayName = 'GeneralDetail';

export default GeneralDetail;
